// /src/pages/PlaybooksPage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Download, ArrowRight, BookOpen } from 'lucide-react';
import { cardCls, headingCls, bodyText, mutedText, pageHeading, pageWrap, sectionInner } from '../components/styles';
import { playbooks } from '../data/playbooks';

export default function PlaybooksPage() {
  return (
    <div className={pageWrap}>
      {/* Hero */}
      <section className="py-12" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl animate-stagger">
            <h1 className={`${pageHeading} mb-4`}><span className="opacity-50">$</span> playbooks</h1>
            <p className={`text-xl ${mutedText} max-w-3xl`}>
              Copy-paste runbooks for security teams. Free, practical, and battle-tested in healthcare, education, and
              government environments.
            </p>
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* Playbook grid */}
      <section className="py-10" style={{ background: 'var(--bg2)' }}>
        <div className={sectionInner}>
          <div className="grid md:grid-cols-2 gap-6 max-w-6xl animate-stagger">
            {playbooks.map((p) => (
              <div key={p.slug} className={`${cardCls} p-6 hover:translate-y-[-2px] transition`}>
                <h3 className={`text-2xl ${headingCls} mb-3`}>{p.title}</h3>
                <p className={`${bodyText} mb-4 leading-relaxed`}>{p.description}</p>

                <div className="flex gap-4 mb-4 text-sm">
                  <span className="font-mono text-xs px-3 py-1 rounded-lg border border-[color:var(--border)] bg-[var(--bg2)] text-[var(--muted)]">
                    {p.time}
                  </span>
                  <span className="font-mono text-xs px-3 py-1 rounded-lg border border-[color:var(--border)] bg-[var(--bg2)] text-[var(--muted)]">
                    {p.audience}
                  </span>
                </div>

                <div className="flex flex-wrap gap-3 pt-4 border-t border-[color:var(--border)]">
                  {p.web && (
                    // Plain <a>, not <Link>: the target is static HTML in public/,
                    // and client-side routing would send it to the 404 route.
                    <a
                      href={p.web}
                      className="flex-1 px-4 py-2 rounded-lg font-semibold inline-flex items-center justify-center gap-2 bg-[var(--accent)] text-[#0b1a21] border border-[color:var(--accent)] hover:opacity-90 transition"
                    >
                      <BookOpen size={18} /> Read online
                    </a>
                  )}
                  {p.formats.includes('PDF') && (
                    <a
                      href={`/playbooks/${p.slug}.pdf`}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-4 py-2 rounded-lg font-semibold inline-flex items-center justify-center gap-2 bg-[var(--bg2)] text-[var(--text)] border border-[color:var(--border)] hover:opacity-90 transition"
                    >
                      <Download size={18} /> PDF
                    </a>
                  )}
                  {p.formats.includes('Markdown') && (
                    <a
                      href={`/playbooks/${p.slug}.md`}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-4 py-2 rounded-lg font-semibold inline-flex items-center justify-center gap-2 bg-[var(--bg2)] text-[var(--text)] border border-[color:var(--border)] hover:opacity-90 transition"
                    >
                      <Download size={18} /> Markdown
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="section-rule" />

      {/* CTA */}
      <section className="py-10" style={{ background: 'var(--bg)' }}>
        <div className={sectionInner}>
          <div className="max-w-6xl">
            <div className="rounded-lg p-8" style={{ background: 'var(--accent)', color: '#0b1a21' }}>
              <h3 className="text-3xl font-bold mb-4">Need a Custom Playbook?</h3>
              <p className="text-lg opacity-90 mb-6">
                I create tailored security runbooks for your organization's specific threats and compliance requirements.
              </p>
              <Link to="/contact" className="px-8 py-4 rounded-lg font-semibold text-[var(--accent)] bg-white hover:opacity-90 transition inline-flex items-center gap-2">
                Schedule a consultation <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
